// Phase 36 — RolePill: small chip showing the buyer/seller perspective
// for the current deal, with an inline "Change role" picker.
//
// • Buyer side → indigo chip "Investor (Buyer side)"
// • Seller side → amber chip "Owner (Seller side)"
// Clicking the chip opens a tiny popover; selecting the other side
// PATCHes the deal and bubbles the updated doc up via `onChange(deal)`
// so the StaleAnalysisBanner can re-flag immediately.

import { useState, useRef, useEffect } from "react";
import { Briefcase, Home, Check, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function RolePill({ deal, onChange }) {
  const role = (deal?.role || "buyer").toLowerCase();
  const isSeller = role === "seller";
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const handlePick = async (next) => {
    if (next === role) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/deals/${deal.id}`, { role: next });
      onChange?.(data);
      toast.success(
        next === "seller"
          ? "Switched to Owner (Seller side)."
          : "Switched to Investor (Buyer side).",
      );
      setOpen(false);
    } catch (e) {
      toast.error("Could not change role. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const chipClass = isSeller
    ? "bg-amber-50 text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100"
    : "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100";
  const Icon = isSeller ? Home : Briefcase;

  return (
    <div ref={wrapRef} className="relative inline-flex items-center" data-testid="deal-role-pill-wrap">
      <button
        type="button"
        data-testid="deal-role-pill"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${chipClass}`}
        title="Change analysis perspective for this deal"
      >
        <Icon className="h-3 w-3" />
        {isSeller ? "Owner (Seller side)" : "Investor (Buyer side)"}
        <span className="ml-1 text-[10px] font-medium opacity-70">change</span>
      </button>

      {open ? (
        <div
          data-testid="deal-role-picker"
          className="absolute left-0 top-full z-30 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-lg"
        >
          <p className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Analyze this deal as
          </p>
          {[
            {
              value: "buyer",
              label: "Investor (Buyer side)",
              hint: "I'm considering buying. Verdicts & math use the counter offer if set.",
              Icon: Briefcase,
              tone: "indigo",
            },
            {
              value: "seller",
              label: "Owner (Seller side)",
              hint: "I'm selling this. Verdicts & math anchor to the asking price.",
              Icon: Home,
              tone: "amber",
            },
          ].map((opt) => {
            const active = opt.value === role;
            return (
              <button
                key={opt.value}
                type="button"
                data-testid={`deal-role-option-${opt.value}`}
                disabled={saving}
                onClick={() => handlePick(opt.value)}
                className={`flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left transition-colors ${
                  active ? "bg-slate-50" : "hover:bg-slate-50"
                }`}
              >
                <opt.Icon
                  className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                    opt.tone === "amber" ? "text-amber-600" : "text-indigo-600"
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900">{opt.label}</p>
                  <p className="text-[11px] text-slate-500">{opt.hint}</p>
                </div>
                {active ? (
                  saving ? (
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  ) : (
                    <Check className="h-4 w-4 text-slate-500" />
                  )
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
